import { useCallback, useMemo } from 'react';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import {
  clientAuthorizedGet,
  clientAuthorizedPost,
  clientAuthorizedPatch,
  clientAuthorizedPut,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';

/**
 * Hook that provides API client functions with companyId automatically included
 * from the active company selection
 */
export function useApiClient() {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const companyId = activeCompanyId;

  const get = useCallback(
    async <T>(
      endpoint: string,
      queryParams?: Record<string, string>,
    ): Promise<T> => {
      return clientAuthorizedGet<T>(endpoint, queryParams, companyId);
    },
    [companyId],
  );

  const post = useCallback(
    async <T>(endpoint: string, data?: Record<string, unknown>): Promise<T> => {
      return clientAuthorizedPost<T>(endpoint, data, companyId);
    },
    [companyId],
  );

  const patch = useCallback(
    async <T>(endpoint: string, data?: Record<string, unknown>): Promise<T> => {
      return clientAuthorizedPatch<T>(endpoint, data, companyId);
    },
    [companyId],
  );

  const put = useCallback(
    async <T>(endpoint: string, data?: Record<string, unknown>): Promise<T> => {
      return clientAuthorizedPut<T>(endpoint, data, companyId);
    },
    [companyId],
  );

  const del = useCallback(
    async <T>(
      endpoint: string,
      data?: Record<string, unknown>,
      queryParams?: Record<string, string>,
    ): Promise<T | null> => {
      return clientAuthorizedDelete<T>(endpoint, data, companyId, queryParams);
    },
    [companyId],
  );

  return useMemo(
    () => ({
      get,
      post,
      patch,
      put,
      delete: del,
      companyId,
      isCompanyInitialized: isInitialized,
    }),
    [get, post, patch, put, del, companyId, isInitialized],
  );
}
